import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import { fetchAllUsers } from "../slices/authSlice";
import {
  fetchWorkSchedules,
  createWorkSchedule,
  editWorkSchedule,
  deleteWorkSchedule,
  WorkSchedule,
} from "../slices/workScheduleSlice";

const weekdays = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];

interface WorkTimesPageProps {
  embedded?: boolean;
}

const formatDateInputLocal = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const toDateInputValue = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return formatDateInputLocal(date);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const normalizeDay = (day: string) => day.toLowerCase().trim();

const isScheduleValidToday = (schedule: WorkSchedule) => {
  const today = new Date().getTime();
  const validFrom = schedule.validFrom ? new Date(schedule.validFrom).getTime() : null;
  const validTo = schedule.validTo ? new Date(schedule.validTo).getTime() : null;

  return validFrom !== null && validFrom <= today && (validTo === null || validTo >= today);
};

const getCurrentScheduleForDay = (
  schedules: WorkSchedule[],
  selectedUserId: string,
  day: string
) => {
  const matchingDay = schedules.filter(
    (schedule) =>
      schedule.userId === selectedUserId &&
      normalizeDay(schedule.dayOfWeek) === normalizeDay(day)
  );

  const historical = matchingDay
    .filter(isScheduleValidToday)
    .sort(
      (a, b) =>
        new Date(b.validFrom || 0).getTime() - new Date(a.validFrom || 0).getTime()
    );

  return historical[0] || matchingDay.find((schedule) => !schedule.validFrom);
};

const getScheduleValue = (schedule: WorkSchedule) => {
  if (schedule.mode === "flex") {
    return `${schedule.hours?.toLocaleString("de-DE", { maximumFractionDigits: 2 }) || 0} h`;
  }

  return `${schedule.startTime || "—"} - ${schedule.endTime || "—"}`;
};

interface WeekScheduleDraft {
  dayOfWeek: string;
  mode: string;
  startTime: string;
  endTime: string;
  hours: string;
  validFrom: string;
  validTo: string;
}

type SaveMessageTone = "muted" | "success" | "warning" | "danger";

interface RowProps {
  draft: WeekScheduleDraft;
  saving: boolean;
  onChange: (day: string, updates: Partial<WeekScheduleDraft>) => void;
}

const buildWeekDrafts = (schedules: WorkSchedule[], selectedUserId: string) =>
  weekdays.map((day) => {
    const schedule = getCurrentScheduleForDay(schedules, selectedUserId, day);

    return {
      dayOfWeek: day,
      mode: schedule?.mode || "fixed",
      startTime: schedule?.startTime || "",
      endTime: schedule?.endTime || "",
      hours: schedule?.hours?.toString() || "",
      validFrom: toDateInputValue(schedule?.validFrom),
      validTo: toDateInputValue(schedule?.validTo),
    };
  });

const WorkTimeRow: React.FC<RowProps> = ({ draft, saving, onChange }) => {

  return (
    <tr>
      <td>{draft.dayOfWeek}</td>

      <td>
        <select
          className="form-select work-schedule-input"
          value={draft.mode}
          disabled={saving}
          onChange={(e) => onChange(draft.dayOfWeek, { mode: e.target.value })}
        >
          <option value="fixed">Feste Zeiten</option>
          <option value="flex">Gleitzeit (Stunden)</option>
        </select>
      </td>

      <td>
        {draft.mode === "fixed" ? (
          <input
            type="time"
            className="form-control work-schedule-input"
            value={draft.startTime}
            disabled={saving}
            onChange={(e) => onChange(draft.dayOfWeek, { startTime: e.target.value })}
          />
        ) : (
          <input
            type="number"
            className="form-control work-schedule-input"
            placeholder="Stunden"
            step="0.25"
            min="0"
            value={draft.hours}
            disabled={saving}
            onChange={(e) => onChange(draft.dayOfWeek, { hours: e.target.value })}
          />
        )}
      </td>

      <td>
        {draft.mode === "fixed" ? (
          <input
            type="time"
            className="form-control work-schedule-input"
            value={draft.endTime}
            disabled={saving}
            onChange={(e) => onChange(draft.dayOfWeek, { endTime: e.target.value })}
          />
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>

      <td>
        <input
          type="date"
          className="form-control work-schedule-input"
          value={draft.validFrom}
          placeholder={formatDateInputLocal(new Date())}
          disabled={saving}
          onChange={(e) => onChange(draft.dayOfWeek, { validFrom: e.target.value })}
        />
      </td>

      <td>
        <input
          type="date"
          className="form-control work-schedule-input"
          value={draft.validTo}
          disabled={saving}
          onChange={(e) => onChange(draft.dayOfWeek, { validTo: e.target.value })}
        />
      </td>
    </tr>
  );
};

interface HistoryRowProps {
  schedule: WorkSchedule;
  canManage: boolean;
  onSave: (schedule: WorkSchedule, updates: {
    dayOfWeek: string;
    startTime: string;
    endTime: string;
    hours: string;
    mode: string;
    validFrom: string;
    validTo: string;
  }) => void;
  onDelete: (id: string) => void;
}

const WorkScheduleHistoryRow: React.FC<HistoryRowProps> = ({
  schedule,
  canManage,
  onSave,
  onDelete,
}) => {
  const [dayOfWeek, setDayOfWeek] = useState(schedule.dayOfWeek);
  const [mode, setMode] = useState<string>(schedule.mode || "fixed");
  const [startTime, setStartTime] = useState(schedule.startTime || "");
  const [endTime, setEndTime] = useState(schedule.endTime || "");
  const [hours, setHours] = useState(schedule.hours?.toString() || "");
  const [validFrom, setValidFrom] = useState(toDateInputValue(schedule.validFrom));
  const [validTo, setValidTo] = useState(toDateInputValue(schedule.validTo));

  useEffect(() => {
    setDayOfWeek(schedule.dayOfWeek);
    setMode(schedule.mode || "fixed");
    setStartTime(schedule.startTime || "");
    setEndTime(schedule.endTime || "");
    setHours(schedule.hours?.toString() || "");
    setValidFrom(toDateInputValue(schedule.validFrom));
    setValidTo(toDateInputValue(schedule.validTo));
  }, [schedule]);

  const reset = () => {
    setDayOfWeek(schedule.dayOfWeek);
    setMode(schedule.mode || "fixed");
    setStartTime(schedule.startTime || "");
    setEndTime(schedule.endTime || "");
    setHours(schedule.hours?.toString() || "");
    setValidFrom(toDateInputValue(schedule.validFrom));
    setValidTo(toDateInputValue(schedule.validTo));
  };

  const hasChanges =
    dayOfWeek !== schedule.dayOfWeek ||
    mode !== (schedule.mode || "fixed") ||
    startTime !== (schedule.startTime || "") ||
    endTime !== (schedule.endTime || "") ||
    hours !== (schedule.hours?.toString() || "") ||
    validFrom !== toDateInputValue(schedule.validFrom) ||
    validTo !== toDateInputValue(schedule.validTo);

  return (
    <tr>
      <td>
        {canManage ? (
          <select
            className="form-select form-select-sm work-schedule-input"
            value={dayOfWeek}
            onChange={(event) => setDayOfWeek(event.target.value)}
          >
            {weekdays.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        ) : (
          schedule.dayOfWeek
        )}
      </td>
      <td>
        {canManage ? (
          <select
            className="form-select form-select-sm work-schedule-input"
            value={mode}
            onChange={(event) => setMode(event.target.value)}
          >
            <option value="fixed">Feste Zeiten</option>
            <option value="flex">Gleitzeit</option>
          </select>
        ) : (
          schedule.mode === "flex" ? "Gleitzeit" : "Feste Zeiten"
        )}
      </td>
      <td>
        {canManage && mode === "fixed" && (
          <div className="d-flex gap-2">
            <input
              type="time"
              className="form-control form-control-sm work-schedule-input"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
            <input
              type="time"
              className="form-control form-control-sm work-schedule-input"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </div>
        )}
        {canManage && mode === "flex" && (
          <input
            type="number"
            className="form-control form-control-sm work-schedule-input"
            step="0.25"
            min="0"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
          />
        )}
        {!canManage && getScheduleValue(schedule)}
      </td>
      <td>
        {canManage ? (
          <input
            type="date"
            className="form-control form-control-sm work-schedule-input"
            value={validFrom}
            onChange={(event) => setValidFrom(event.target.value)}
          />
        ) : (
          formatDate(schedule.validFrom)
        )}
      </td>
      <td>
        {canManage ? (
          <input
            type="date"
            className="form-control form-control-sm work-schedule-input"
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
          />
        ) : (
          schedule.validTo ? formatDate(schedule.validTo) : "Aktuell"
        )}
      </td>
      {canManage && (
        <td>
          <div className="d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              disabled={!hasChanges}
              onClick={() => {
                onSave(schedule, {
                  dayOfWeek,
                  startTime,
                  endTime,
                  hours,
                  mode,
                  validFrom,
                  validTo,
                });
              }}
            >
              Speichern
            </button>
            <button
              className="btn btn-secondary btn-sm"
              disabled={!hasChanges}
              onClick={reset}
            >
              Zurücksetzen
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(schedule._id)}>
              Löschen
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

const WorkTimesPage: React.FC<WorkTimesPageProps> = ({ embedded = false }) => {
  const dispatch = useDispatch<AppDispatch>();

  const authUser = useSelector((state: RootState) => state.auth.user);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);
  const schedules = useSelector((state: RootState) => state.work.schedules);
  const canManageSchedules = authUser?.role === "admin";

  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [weekDrafts, setWeekDrafts] = useState<WeekScheduleDraft[]>([]);
  const [hasWeekChanges, setHasWeekChanges] = useState(false);
  const [weekSaving, setWeekSaving] = useState(false);
  const [weekMessage, setWeekMessage] = useState("");
  const [weekMessageTone, setWeekMessageTone] = useState<SaveMessageTone>("muted");

  useEffect(() => {
    if (authUser) {
      if (authUser.role === "admin" && allUsers.length > 0) {
        setSelectedUserId(allUsers[0]._id);
      } else {
        setSelectedUserId(authUser._id);
      }
    }
  }, [authUser, allUsers]);

  useEffect(() => {
    dispatch(fetchWorkSchedules());
  }, [dispatch]);

  useEffect(() => {
    if (canManageSchedules) {
      dispatch(fetchAllUsers());
    }
  }, [canManageSchedules, dispatch]);

  const selectedUserSchedules = useMemo(() => {
    return schedules
      .filter((schedule) => schedule.userId === selectedUserId)
      .sort((a, b) => {
        const currentA = isScheduleValidToday(a);
        const currentB = isScheduleValidToday(b);
        if (currentA !== currentB) return currentA ? -1 : 1;

        const fromDiff =
          new Date(b.validFrom || 0).getTime() - new Date(a.validFrom || 0).getTime();
        if (fromDiff !== 0) return fromDiff;

        return weekdays.indexOf(a.dayOfWeek) - weekdays.indexOf(b.dayOfWeek);
      });
  }, [schedules, selectedUserId]);

  useEffect(() => {
    if (!selectedUserId || hasWeekChanges || weekSaving) return;
    setWeekDrafts(buildWeekDrafts(schedules, selectedUserId));
  }, [hasWeekChanges, schedules, selectedUserId, weekSaving]);

  const handleWeekDraftChange = useCallback((
    day: string,
    updates: Partial<WeekScheduleDraft>
  ) => {
    setWeekDrafts((current) =>
      current.map((draft) =>
        draft.dayOfWeek === day ? { ...draft, ...updates } : draft
      )
    );
    setHasWeekChanges(true);
    setWeekMessage("");
    setWeekMessageTone("muted");
  }, []);

  const handleSaveWeek = useCallback(async () => {
    if (!canManageSchedules || !selectedUserId || weekSaving) return;

    const filledDrafts = weekDrafts.filter((draft) => {
      if (draft.mode === "fixed") {
        return draft.startTime || draft.endTime || draft.validFrom || draft.validTo;
      }

      return draft.hours || draft.validFrom || draft.validTo;
    });

    const invalidDraft = filledDrafts.find((draft) => {
      if (draft.mode === "fixed") {
        return !draft.startTime || !draft.endTime || !draft.validFrom;
      }

      return !draft.hours || Number.isNaN(Number(draft.hours)) || !draft.validFrom;
    });

    if (filledDrafts.length === 0) {
      setWeekMessage("Keine Arbeitszeiten zum Speichern.");
      setWeekMessageTone("warning");
      return;
    }

    if (invalidDraft) {
      setWeekMessage(`${invalidDraft.dayOfWeek}: Bitte Arbeitszeit und "Gültig ab" ausfüllen.`);
      setWeekMessageTone("warning");
      return;
    }

    setWeekSaving(true);
    setWeekMessage("Speichert...");
    setWeekMessageTone("muted");

    try {
      await Promise.all(
        filledDrafts.map((draft) => {
          const existing = getCurrentScheduleForDay(schedules, selectedUserId, draft.dayOfWeek);
          const payload: any = {
            userId: selectedUserId,
            dayOfWeek: draft.dayOfWeek,
            startTime: draft.mode === "fixed" ? draft.startTime : null,
            endTime: draft.mode === "fixed" ? draft.endTime : null,
            hours: draft.mode === "flex" ? parseFloat(draft.hours) || 0 : null,
            mode: draft.mode,
            validFrom: draft.validFrom,
            validTo: draft.validTo || null,
          };

          if (existing) {
            return dispatch(editWorkSchedule({ id: existing._id, ...payload })).unwrap();
          }

          return dispatch(createWorkSchedule(payload)).unwrap();
        })
      );

      await dispatch(fetchWorkSchedules()).unwrap();
      setHasWeekChanges(false);
      setWeekMessage("Gespeichert");
      setWeekMessageTone("success");
    } catch (error) {
      setWeekMessage("Speichern fehlgeschlagen");
      setWeekMessageTone("danger");
    } finally {
      setWeekSaving(false);
    }
  }, [canManageSchedules, dispatch, schedules, selectedUserId, weekDrafts, weekSaving]);

  const handleHistorySave = (
    schedule: WorkSchedule,
    updates: {
      dayOfWeek: string;
      startTime: string;
      endTime: string;
      hours: string;
      mode: string;
      validFrom: string;
      validTo: string;
    }
  ) => {
    if (!canManageSchedules) return;

    dispatch(
      editWorkSchedule({
        id: schedule._id,
        userId: schedule.userId,
        dayOfWeek: updates.dayOfWeek,
        startTime: updates.mode === "fixed" ? updates.startTime : null,
        endTime: updates.mode === "fixed" ? updates.endTime : null,
        hours: updates.mode === "flex" ? parseFloat(updates.hours) || 0 : null,
        mode: updates.mode,
        validFrom: updates.validFrom,
        validTo: updates.validTo || null,
      })
    );
  };

  const handleDelete = (id: string) => {
    if (!canManageSchedules) return;
    dispatch(deleteWorkSchedule(id));
  };

  return (
    <div className={embedded ? "" : "container mt-4"}>
      {canManageSchedules && (
        <>
          <h2 className={embedded ? "h4 mb-3" : undefined}>Soll-Arbeitszeiten</h2>

          <div className="mb-3">
            <label className="form-label">Nutzer auswählen:</label>
            <select
              className="form-select"
              value={selectedUserId}
              disabled={weekSaving}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                setHasWeekChanges(false);
                setWeekMessage("");
                setWeekMessageTone("muted");
              }}
            >
              {allUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username} {user._id === authUser._id ? "(Ich)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
            {weekMessage && (
              <span className={`small text-${weekMessageTone}`}>
                {weekMessage}
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!hasWeekChanges || weekSaving}
              onClick={handleSaveWeek}
            >
              {weekSaving ? "Speichert..." : "Änderungen speichern"}
            </button>
          </div>

          {hasWeekChanges && !weekMessage && (
            <div className="small text-muted text-end mb-2">
              Ungespeicherte Änderungen
            </div>
          )}

          <table className="table">
            <thead>
              <tr>
                <th>Wochentag</th>
                <th>Modus</th>
                <th>Start / Stunden</th>
                <th>Ende</th>
                <th>Gültig ab</th>
                <th>Gültig bis</th>
              </tr>
            </thead>
            <tbody>
              {weekDrafts.map((draft) => (
                <WorkTimeRow
                  key={draft.dayOfWeek}
                  draft={draft}
                  saving={weekSaving}
                  onChange={handleWeekDraftChange}
                />
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className={canManageSchedules ? "mt-4" : ""}>
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h5 className="mb-0">Arbeitszeiten Historie</h5>
          <span className="badge text-bg-light border">{selectedUserSchedules.length}</span>
        </div>

        {selectedUserSchedules.length === 0 ? (
          <p className="text-muted">Keine WorkSchedules hinterlegt.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Wochentag</th>
                  <th>Modus</th>
                  <th>Zeit / Stunden</th>
                  <th>Gültig Ab</th>
                  <th>Gültig Bis</th>
                  {canManageSchedules && <th>Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {selectedUserSchedules.map((schedule) => (
                  <WorkScheduleHistoryRow
                    key={schedule._id}
                    schedule={schedule}
                    canManage={canManageSchedules}
                    onSave={handleHistorySave}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkTimesPage;
